import { useState, useEffect } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Building2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import { toast } from '@/hooks/use-toast';

export const AgencyLogin: React.FC = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) {
      const isAgency = !!(user as any)?.is_agency;
      if (isAgency) {
        navigate('/home/agency/dashboard');
      } else {
        setError('Access Denied: This login is only for agency users. Please use the regular login page.');
        setEmail('');
        setPassword('');
      }
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password, false);

      if (result.success) {
        const currentUser = (result as any).user || JSON.parse(localStorage.getItem('user') || '{}');
        
        const isAgency = currentUser?.is_agency === true || 
                        currentUser?.is_agency === 1 || 
                        currentUser?.is_agency === '1' ||
                        currentUser?.is_agency === 'true' ||
                        Boolean(currentUser?.is_agency);
        
        
        if (isAgency) {
          toast({
            title: 'Welcome back!',
            description: "You've been signed in successfully.",
          });
          navigate('/home/agency/dashboard');
        } else {
          setError('Access Denied: This login is only for agency users. Please use the regular login page.');
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refreshToken');
          if (typeof window !== 'undefined' && (window as any).tokenRefreshService) {
            (window as any).tokenRefreshService.clearTokens();
          }
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error: any) {
      setError(friendlyMessage(error, "We couldn't log you in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-2">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Agency Login</CardTitle>
            <CardDescription>
              Sign in to your agency account to manage referrals and view compensation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="agency@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <Link
                  to="/forgot-password"
                  className="hover:text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t">
              <div className="text-center text-sm text-muted-foreground space-y-2">
                <p>Not an agency user?</p>
                <div className="flex items-center justify-center gap-2">
                  <Link
                    to="/auth"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Go to regular login
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

